import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';

export interface Conversation {
  id: number;
  title: string;
  created_at: string;
  updated_at: string;
  message_count: number;
  last_message?: {
    content: string;
    role: 'user' | 'assistant';
    created_at: string;
  };
}

export interface Message {
  id: number;
  role: 'user' | 'assistant';
  content: string;
  created_at: string;
  query?: {
    raw_query: string;
    extracted_at: string;
  };
  result?: {
    success: boolean;
    row_count: number;
    data: any[];
    note?: string;
    error?: string;
  };
  suggested_queries?: string[];
}

export interface ConversationDetail {
  id: number;
  title: string;
  created_at: string;
  messages: Message[];
}

export interface SendMessageResponse {
  success: boolean;
  data: {
    message: string;
    query?: {
      raw_query: string;
      extracted_at: string;
    };
    result?: {
      success: boolean;
      row_count: number;
      data: any[];
      note?: string;
      error?: string;
    };
    suggested_queries?: string[];
  };
}

export interface ChatContext {
  jobId?: number;
  customerId?: number;
  jobRefParent?: number;
  transactionData?: any;
  [key: string]: any;
}

@Injectable({
  providedIn: 'root'
})
export class ChatService {
  private apiUrl = environment.api + 'chat';
  private conversationsSubject = new BehaviorSubject<Conversation[]>([]);
  private currentConversationSubject = new BehaviorSubject<ConversationDetail | null>(null);
  private messagesSubject = new BehaviorSubject<Message[]>([]);
  private loadingSubject = new BehaviorSubject<boolean>(false);
  private messageCountSubject = new BehaviorSubject<number>(0);
  private hasReachedLimitSubject = new BehaviorSubject<boolean>(false);
  private conversationLimitReachedSubject = new BehaviorSubject<boolean>(false);
  private contextSubject = new BehaviorSubject<ChatContext>({});

  public conversations$ = this.conversationsSubject.asObservable();
  public currentConversation$ = this.currentConversationSubject.asObservable();
  public messages$ = this.messagesSubject.asObservable();
  public loading$ = this.loadingSubject.asObservable();
  public messageCount$ = this.messageCountSubject.asObservable();
  public hasReachedLimit$ = this.hasReachedLimitSubject.asObservable();
  public conversationLimitReached$ = this.conversationLimitReachedSubject.asObservable();
  public context$ = this.contextSubject.asObservable();

  private readonly MESSAGE_LIMIT = 100;

  constructor(private http: HttpClient) {
    this.loadConversations();
  }

  /**
   * Load all conversations for the user
   */
  loadConversations(): void {
    this.setLoading(true);
    this.http.get<any>(`${this.apiUrl}/conversations`)
      .pipe(
        tap(response => {
          if (response.success) {
            this.conversationsSubject.next(response.data || []);
          }
        }),
        catchError(error => this.handleError(error))
      )
      .subscribe(() => this.setLoading(false));
  }

  /**
   * Get all conversations (observable)
   */
  getConversations(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/conversations`);
  }

  /**
   * Create a new conversation
   */
  createConversation(title?: string, context?: any): Observable<any> {
    const payload: any = {};
    if (title) payload.title = title;
    if (context) payload.context = context;

    return this.http.post<any>(`${this.apiUrl}/conversations`, payload)
      .pipe(
        tap(response => {
          if (response.success) {
            this.loadConversations(); // Refresh list
          }
        }),
        catchError(error => this.handleError(error))
      );
  }

  /**
   * Get specific conversation with all messages
   */
  getConversation(conversationId: number): Observable<any> {
    this.setLoading(true);
    return this.http.get<any>(`${this.apiUrl}/conversations/${conversationId}`)
      .pipe(
        tap(response => {
          if (response.success) {
            this.currentConversationSubject.next(response.data);
            const messages = response.data.messages || [];
            this.messagesSubject.next(messages);
            this.updateMessageLimitStatus(messages.length);
          }
        }),
        catchError(error => this.handleError(error))
      );
  }

  /**
   * Load conversation and update state
   */
  loadConversation(conversationId: number): void {
    this.getConversation(conversationId)
      .subscribe(() => this.setLoading(false));
  }

  /**
   * Update message count and limit status
   */
  private updateMessageLimitStatus(messageCount: number): void {
    this.messageCountSubject.next(messageCount);
    const hasReachedLimit = messageCount >= this.MESSAGE_LIMIT;
    this.hasReachedLimitSubject.next(hasReachedLimit);
    this.conversationLimitReachedSubject.next(hasReachedLimit);
  }

  /**
   * Send message and get response
   */
  sendMessage(conversationId: number, message: string): Observable<SendMessageResponse> {
    if (this.hasReachedMessageLimit()) {
      return throwError(() => new Error('This conversation has reached the 100 message limit. Please start a new conversation.'));
    }

    this.setLoading(true);
    return this.http.post<SendMessageResponse>(
      `${this.apiUrl}/conversations/${conversationId}/messages`,
      { message }
    )
      .pipe(
        tap(response => {
          if (response.success) {
            // Don't reload conversation here - let component handle message updates with suggested_queries
            this.setLoading(false);
          }
        }),
        catchError(error => {
          this.setLoading(false);
          return this.handleError(error);
        })
      );
  }

  /**
   * Update conversation title
   */
  updateConversation(conversationId: number, title: string): Observable<any> {
    return this.http.patch<any>(
      `${this.apiUrl}/conversations/${conversationId}`,
      { title }
    )
      .pipe(
        tap(response => {
          if (response.success) {
            this.loadConversations(); // Refresh list
          }
        }),
        catchError(error => this.handleError(error))
      );
  }

  /**
   * Delete conversation
   */
  deleteConversation(conversationId: number): Observable<any> {
    return this.http.delete<any>(`${this.apiUrl}/conversations/${conversationId}`)
      .pipe(
        tap(response => {
          if (response.success) {
            this.loadConversations(); // Refresh list
            this.currentConversationSubject.next(null);
            this.messagesSubject.next([]);
          }
        }),
        catchError(error => this.handleError(error))
      );
  }

  /**
   * Add message to current conversation (for optimistic UI)
   */
  addMessageToUI(message: Message): void {
    const currentMessages = this.messagesSubject.value;
    this.messagesSubject.next([...currentMessages, message]);
  }

  /**
   * Get current conversation value
   */
  getCurrentConversation(): ConversationDetail | null {
    return this.currentConversationSubject.value;
  }

  /**
   * Get current messages value
   */
  getCurrentMessages(): Message[] {
    return this.messagesSubject.value;
  }

  /**
   * Check if messages exceed limit (100 messages = max)
   */
  hasReachedMessageLimit(): boolean {
    const messages = this.messagesSubject.value;
    return messages.length >= 100;
  }

  /**
   * Set chat context for better query generation
   * Context can include job ID, customer ID, transaction data, etc.
   */
  setContext(context: ChatContext): void {
    const currentContext = this.contextSubject.value;
    this.contextSubject.next({ ...currentContext, ...context });
  }

  /**
   * Get current context
   */
  getContext(): ChatContext {
    return this.contextSubject.value;
  }

  /**
   * Update context with transaction data
   */
  setTransactionContext(jobId: number, transactionData: any, jobRefParent?: number): void {
    this.setContext({
      jobId,
      jobRefParent,
      transactionData
    });
  }

  /**
   * Clear context
   */
  clearContext(): void {
    this.contextSubject.next({});
  }

  /**
   * Get context as query parameters string for API calls
   */
  getContextQueryString(): string {
    const context = this.contextSubject.value;
    const params = new URLSearchParams();

    if (context.jobId) {
      params.append('job_id', context.jobId.toString());
    }
    if (context.customerId) {
      params.append('customer_id', context.customerId.toString());
    }
    if (context.jobRefParent) {
      params.append('job_ref_parent', context.jobRefParent.toString());
    }

    return params.toString() ? `?${params.toString()}` : '';
  }

  /**
   * Create conversation with transaction context
   */
  createConversationWithContext(title: string, context: ChatContext): Observable<any> {
    const payload: any = { title, context };
    return this.http.post<any>(`${this.apiUrl}/conversations`, payload)
      .pipe(
        tap(response => {
          if (response.success) {
            this.setContext(context);
            this.loadConversations();
          }
        }),
        catchError(error => this.handleError(error))
      );
  }

  /**
   * Send message with context for better AI responses
   */
  sendMessageWithContext(conversationId: number, message: string, context?: ChatContext): Observable<SendMessageResponse> {
    if (context) {
      this.setContext(context);
    }

    if (this.hasReachedMessageLimit()) {
      return throwError(() => new Error('This conversation has reached the 100 message limit. Please start a new conversation.'));
    }

    this.setLoading(true);
    const payload: any = { message };

    // Include context in the request for better query generation
    const currentContext = this.contextSubject.value;
    if (Object.keys(currentContext).length > 0) {
      payload.context = currentContext;
    }

    return this.http.post<SendMessageResponse>(
      `${this.apiUrl}/conversations/${conversationId}/messages`,
      payload
    )
      .pipe(
        tap(response => {
          if (response.success) {
            this.loadConversation(conversationId);
          }
        }),
        catchError(error => this.handleError(error))
      );
  }

  /**
   * Private helper methods
   */
  private setLoading(loading: boolean): void {
    this.loadingSubject.next(loading);
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = 'An error occurred';

    if (error.error instanceof ErrorEvent) {
      errorMessage = `Error: ${error.error.message}`;
    } else {
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
      if (error.error && error.error.message) {
        errorMessage = error.error.message;
      }
    }

    console.error(errorMessage);
    return throwError(() => new Error(errorMessage));
  }
}
